import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Anchor, Container } from 'components/base';
import {
  CloudinaryPicture,
  ScriptMarkupFaq,
  ScriptMarkupVideo,
  WIDTH_LADDERS,
  getCloudinaryUrl,
} from 'components/rich';

import { Head, Layout } from '../components/layout';
import NewsletterTile from '../components/newsletter/newsletterTile';
import PdpIcon from '../components/product/pdpIcon';
import ProductFaq from '../components/product/productFaq';
import tracker from '../lib/tracker';
import SETTINGS from '../settings';

// Marketing landing page for colored print backgrounds (iOS-only feature).
// Target of the app's "Learn more" link and the SEO page for "colorful square prints".
// Layout follows "Colorful Square Prints — Redesign" / "— Mobile" in design/website.pen.
// See docs/colorful-square-prints.md.

const SHOP_URL = '/square-prints';

// Use the Colorful CPP URL here once PBP-182 publishes it. Preserve its query parameters.
const APP_STORE_URL = new URL(SETTINGS.APP_STORE_URL);
APP_STORE_URL.searchParams.set('utm_source', 'website');
APP_STORE_URL.searchParams.set('utm_medium', 'landing');
APP_STORE_URL.searchParams.set('utm_campaign', 'colorful_square');

// A sample of the app's suggestion palette (parabo-swift PageBackgroundColor+Palette.swift).
// Deliberately unnamed and uncounted on the page: the full palette is still pending
// physical print approval and may change.
const SAMPLE_COLORS = ['#C1CDB9', '#A96F59', '#C2D4DF', '#EEE0AC', '#66505F', '#D5B0AD'];
const [SAGE, TERRACOTTA, BLUE, BUTTER, PLUM, ROSE] = SAMPLE_COLORS;

// Unsplash photos prepared for the photo book guide, see docs/unsplash-assets.md.
const photo = (name) => `landing/photo-book-guide/${name}`;

// Print stages are drawn on the design canvas. Geometry is [x, y, width] in design px, converted
// to percentages of the stage so the fan scales with its column. `phone` is omitted for prints
// the mobile frame drops.
const HERO_STAGE = { desk: [632, 600], phone: [350, 330] };
const HERO_PRINTS = [
  {
    imageKey: photo('forest'),
    color: SAGE,
    rotate: 7,
    desk: [6, 70, 220],
    phone: [0, 24, 130],
    alt: 'Square Print of a forest path with a colored border',
  },
  {
    imageKey: photo('grandpa-blossom'),
    color: ROSE,
    rotate: -6,
    desk: [385, 40, 230],
    phone: [214, 28, 136],
    alt: 'Square Print of a grandfather under a blossoming tree with a colored border',
  },
  {
    imageKey: photo('cake'),
    color: BUTTER,
    rotate: -5,
    desk: [46, 368, 190],
    alt: 'Square Print of a birthday cake with a colored border',
    small: true,
  },
  {
    imageKey: photo('sunset'),
    color: PLUM,
    rotate: 5,
    desk: [400, 320, 200],
    phone: [226, 186, 120],
    alt: 'Square Print of a sunset with a colored border',
    small: true,
  },
  {
    imageKey: photo('coast'),
    color: BLUE,
    rotate: -2,
    desk: [160, 112, 320],
    phone: [76, 60, 196],
    alt: 'Square Print of a rocky coast with a colored border',
    priority: true,
  },
];

// Mirrors the app's color picker for the coast print: white plus the suggested shades.
const SUGGESTED_SWATCHES = ['#FFFFFF', BLUE, SAGE, PLUM, BUTTER];
const SUGGESTED_SELECTED = BLUE;

const CLOSING_STAGE = { desk: [520, 300], phone: [350, 190] };
const CLOSING_PRINTS = [
  {
    imageKey: photo('dog'),
    color: TERRACOTTA,
    rotate: 6,
    desk: [20, 60, 210],
    phone: [14, 40, 130],
  },
  {
    imageKey: photo('beach-selfie'),
    color: BUTTER,
    rotate: -5,
    desk: [290, 40, 210],
    phone: [210, 30, 130],
  },
  { imageKey: photo('kids'), color: BLUE, rotate: -1, desk: [150, 30, 230], phone: [100, 20, 150] },
];

const PALETTE_IMAGE_KEY = photo('hat');

// Screen recording of the Suggest Colors flow, published through parabo-product-images
// (images/products/colorful-square-prints/1.mp4 -> mp4 + webm + big/small posters on the CDN).
const DEMO_VIDEO_KEY = 'colorful-square-prints/1';
const DEMO_VIDEO_ALT =
  'Screen recording of the Parabo Press app: Suggest Colors gives every Square Print a colored border in one tap';
// Bump when the master in parabo-product-images is replaced. Search Console flags a bare
// YYYY-MM-DD as "missing a timezone", so keep the full ISO 8601 datetime with offset.
const DEMO_VIDEO_UPLOAD_DATE = '2026-09-08T00:00:00+00:00';
const DEMO_VIDEO_DURATION = 'PT15S';

// Short verified claims (docs/colorful-square-prints.md). No inch sizes, see docs.
const FACTS = [
  { icon: 'tag', title: 'Same price', text: 'as a white border' },
  { icon: 'layers', title: 'Same paper', text: 'thick, matte, FSC certified' },
  { icon: 'ruler', title: 'All three sizes', text: 'of Square Prints' },
  { icon: 'square', title: 'Bordered prints', text: 'the color fills the border' },
];

const STEPS = [
  {
    title: 'Pick your photos',
    text: 'Open the Parabo Press app, choose Square Prints and add your photos. Keep the classic border on, that is where the color goes.',
  },
  {
    title: 'One tap colors them all',
    text: 'Tap Suggest Colors. The app studies every photo in your set and gives each print a border that belongs to it. No picking, no guessing, one tap.',
  },
  {
    title: 'Adjust print by print',
    text: 'Love it? Order. Want a change? Tap any print to swap its color or set it back to white. Every print is its own, so mix and match freely.',
  },
];
// The Suggest Colors step carries the page's point, the design fills its number.
const HIGHLIGHTED_STEP = 1;

// FAQPage JSON-LD source and the rendered accordion. Plain text, ProductFaq renders it as HTML.
const FAQ = [
  {
    question: 'Can I choose background colors on the website?',
    answer:
      'Not yet. Colored backgrounds are available in the Parabo Press iOS app. On the website you can order Square Prints with a classic white border or without a border.',
  },
  {
    question: 'Does a colored background cost more?',
    answer: 'No. Colorful Square Prints cost the same as Square Prints with a white border.',
  },
  {
    question: 'Which sizes can have a colored background?',
    answer:
      'The same sizes as regular Square Prints. The color fills the border, so it is available for bordered prints only. Prints without a border have nothing to color.',
  },
  {
    question: 'Can I mix white and colored prints in one set?',
    answer:
      'Yes. Every print has its own background. Color some, leave others white, and change your mind as often as you like before you order.',
  },
  {
    question: 'How does Suggest Colors work?',
    answer:
      'The app looks at each photo and picks a shade from its palette that goes with it. Suggest Colors only touches prints you have not colored yourself. You can change any suggestion or reset the whole set back to white.',
  },
  {
    question: 'How does the app know which color fits my photo?',
    answer:
      "It starts with a little color science. The app breaks the photo down into its dominant colors and weighs how much of the frame each one covers. Those shades are compared in a perceptual color space, the kind built to mirror how eyes see rather than how screens store pixels. Every color in our palette is then scored on two things: how much it harmonizes with the photo, and how cleanly it separates from the photo's edges. Only colors that pass both tests make the cut, each one distinct from the others and from plain white. Black and white photos get muted shades only, so the photo stays the quiet hero. All of it runs on your iPhone in an instant, nothing is uploaded.",
  },
  {
    question: 'Is the color printed or is it colored paper?',
    answer:
      'The color is printed, on the same thick, matte, FSC certified paper as every Square Print. Your photo and its colored border come off the press together.',
  },
];

const trackCtaClick = (cta, location) => () => {
  tracker.onCustomEvent('colorful_square_prints_cta_click', { cta, location });
};

const AppStoreBadge = ({ location }) => (
  <Anchor
    href={APP_STORE_URL.href}
    className="csp-badge csp-cta-app"
    targetBlank
    onClick={trackCtaClick('app_store', location)}
  >
    <img
      src="/static/img/app-store-badge.svg"
      alt="Download Parabo Press on the App Store"
      width="147"
      height="49"
    />
  </Anchor>
);

AppStoreBadge.propTypes = {
  location: PropTypes.oneOf(['hero', 'closing']).isRequired,
};

// Desktop handoff to the phone. The code encodes the plain App Store URL (no UTM) so it stays
// scannable at 66px, the tile links to the tracked URL for keyboard and screen reader users.
const QrHandoff = ({ location }) => (
  <Anchor
    href={APP_STORE_URL.href}
    className="csp-qr csp-cta-desk"
    targetBlank
    onClick={trackCtaClick('app_store', location)}
  >
    <span className="csp-qr-code">
      <img
        src="/static/img/colorful-square-prints-app-qr.svg"
        alt="QR code for the Parabo Press app on the App Store"
        width="66"
        height="66"
      />
    </span>
    <span className="csp-qr-label">
      <span className="csp-qr-title">Get the iPhone app</span>
      <span className="csp-qr-text">
        Scan with your iPhone camera
        <br />
        to open the App Store
      </span>
    </span>
  </Anchor>
);

QrHandoff.propTypes = {
  location: PropTypes.oneOf(['hero', 'closing']).isRequired,
};

// Desktop: QR code. Below lg: the App Store badge. No web path, the feature is app only.
const AppCta = ({ location }) => (
  <div className="csp-cta">
    <QrHandoff location={location} />
    <AppStoreBadge location={location} />
  </div>
);

AppCta.propTypes = {
  location: PropTypes.oneOf(['hero', 'closing']).isRequired,
};

const percent = (value, total) => `${(value / total) * 100}%`;

const stageVars = (geometry, [width, height], prefix) =>
  geometry
    ? {
        [`--${prefix}-x`]: percent(geometry[0], width),
        [`--${prefix}-y`]: percent(geometry[1], height),
        [`--${prefix}-w`]: percent(geometry[2], width),
      }
    : {};

// A bordered Square Print: the padding is the printed border, the color is set inline.
const ColoredPrint = ({ imageKey, color, sizes, alt, priority, eager, className, style }) => (
  <div className={classNames('csp-print', className)} style={{ backgroundColor: color, ...style }}>
    <div className="csp-print-photo">
      <CloudinaryPicture
        imageKey={imageKey}
        widths={WIDTH_LADDERS.landing}
        sizes={sizes}
        alt={alt ?? ''}
        priority={priority}
        loading={priority || eager ? undefined : 'lazy'}
      />
    </div>
  </div>
);

ColoredPrint.propTypes = {
  imageKey: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  sizes: PropTypes.string.isRequired,
  alt: PropTypes.string,
  priority: PropTypes.bool,
  eager: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

const PrintStage = ({ prints, stage, sizes, eager, className, children }) => (
  <div
    className={classNames('csp-stage', className)}
    style={{
      '--desk-ratio': `${stage.desk[0]} / ${stage.desk[1]}`,
      '--phone-ratio': `${stage.phone[0]} / ${stage.phone[1]}`,
    }}
  >
    {prints.map((print) => (
      <ColoredPrint
        key={print.imageKey}
        imageKey={print.imageKey}
        color={print.color}
        sizes={sizes}
        alt={print.alt}
        priority={print.priority}
        eager={eager}
        className={classNames({
          'csp-print--small': print.small,
          'csp-print--desk-only': !print.phone,
        })}
        style={{
          '--rotate': `${print.rotate}deg`,
          ...stageVars(print.desk, stage.desk, 'desk'),
          ...stageVars(print.phone, stage.phone, 'phone'),
        }}
      />
    ))}
    {children}
  </div>
);

PrintStage.propTypes = {
  prints: PropTypes.arrayOf(PropTypes.object).isRequired,
  stage: PropTypes.shape({ desk: PropTypes.array, phone: PropTypes.array }).isRequired,
  sizes: PropTypes.string.isRequired,
  // Above the fold, load every print right away.
  eager: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

// Illustrative copy of the app's color picker, not live app output.
const SuggestedChip = () => (
  <div className="csp-chip" aria-hidden="true">
    <span className="csp-chip-head">
      <PdpIcon name="sparkles" size={14} />
      Suggested for this photo
    </span>
    <span className="csp-chip-swatches">
      {SUGGESTED_SWATCHES.map((color) => (
        <span
          key={color}
          className={classNames('csp-swatch', { selected: color === SUGGESTED_SELECTED })}
          style={{ '--swatch': color }}
        />
      ))}
    </span>
  </div>
);

// Autoplaying loop longer than 5 s, so it needs a pause control (WCAG 2.2.2).
const DemoVideo = () => {
  const videoRef = useRef(null);
  const [paused, setPaused] = useState(false);

  // Follow the element, autoplay can be blocked or the browser can pause it on its own.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const sync = () => setPaused(video.paused);
    sync();
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    return () => {
      video.removeEventListener('play', sync);
      video.removeEventListener('pause', sync);
    };
  }, []);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      return;
    }
    video.pause();
  };

  return (
    <figure className="csp-demo">
      <div className="csp-demo-frame">
        <CloudinaryPicture
          assetType="video"
          prefix="products/"
          imageKey={DEMO_VIDEO_KEY}
          type="big"
          loop
          videoRef={videoRef}
          className="csp-demo-video"
          alt={DEMO_VIDEO_ALT}
        />
        <button
          type="button"
          className="csp-demo-toggle"
          aria-label={paused ? 'Play video' : 'Pause video'}
          onClick={toggle}
        >
          <PdpIcon name={paused ? 'play' : 'pause'} size={16} />
        </button>
      </div>
      <figcaption className="csp-demo-caption">Suggest Colors in the Parabo Press app</figcaption>
    </figure>
  );
};

const Hero = () => (
  <section className="csp-hero">
    <Container className="csp-hero-grid">
      <div className="csp-hero-copy">
        <p className="csp-eyebrow">
          <span className="csp-eyebrow-dot" aria-hidden="true" />
          NOW IN THE iOS APP
        </p>
        <h1 className="csp-h1">Colorful Square Prints</h1>
        <p className="csp-lead">
          Meet Colorful Square Prints: the classic white border, now in a shade that fits each
          photo. One tap colors the whole set. Tweak any of them yourself.
        </p>
        <AppCta location="hero" />
      </div>

      <PrintStage
        prints={HERO_PRINTS}
        stage={HERO_STAGE}
        sizes="(max-width: 991px) 55vw, 270px"
        eager
        className="csp-hero-stage"
      >
        <SuggestedChip />
      </PrintStage>

      {/* The website cannot color borders, so the shop link is framed as the fallback for
          visitors without an iPhone rather than as a competing CTA. */}
      <div className="csp-fallback">
        <p>
          No iPhone? The Square Prints you know and love, with the classic white border, are still
          here. Same as always.
        </p>
        <Link href={SHOP_URL} className="csp-link" onClick={trackCtaClick('shop', 'hero')}>
          Shop Square Prints
          <PdpIcon name="arrowRight" size={16} />
        </Link>
      </div>
    </Container>
  </section>
);

const Facts = () => (
  <section className="csp-facts">
    <Container>
      <ul className="csp-facts-list">
        {FACTS.map((fact) => (
          <li key={fact.title} className="csp-fact">
            <span className="csp-fact-icon">
              <PdpIcon name={fact.icon} size={18} />
            </span>
            <span className="csp-fact-text">
              <strong>{fact.title}</strong>
              <span>{fact.text}</span>
            </span>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const HowItWorks = () => (
  <section className="csp-section csp-how">
    <Container className="csp-how-grid">
      <div className="csp-how-head">
        <p className="csp-label">How it works</p>
        <h2 className="csp-h2">One tap colors the whole set</h2>
      </div>
      <DemoVideo />
      <ol className="csp-steps">
        {STEPS.map((step, index) => (
          <li
            key={step.title}
            className={classNames('csp-step', { highlighted: index === HIGHLIGHTED_STEP })}
          >
            <span className="csp-step-number" aria-hidden="true">
              {index + 1}
            </span>
            <div className="csp-step-text">
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </Container>
  </section>
);

const Palette = () => (
  <section className="csp-section csp-palette">
    <Container>
      <div className="csp-palette-head">
        <div>
          <p className="csp-label">The palette</p>
          <h2 className="csp-h2">One photo, many moods</h2>
        </div>
        <p className="csp-body">
          A curated palette of soft colors made for print. Warm or cool, quiet or bold, the border
          sets the tone and the photo stays the star.
        </p>
      </div>
      <div className="csp-palette-row">
        {SAMPLE_COLORS.map((color) => (
          <ColoredPrint
            key={color}
            imageKey={PALETTE_IMAGE_KEY}
            color={color}
            sizes="(max-width: 767px) 30vw, 150px"
          />
        ))}
      </div>
    </Container>
  </section>
);

const Closing = () => (
  <section className="csp-closing">
    <Container className="csp-closing-grid">
      <div className="csp-closing-copy">
        <h2 className="csp-h2">Ready to add some color?</h2>
        <p className="csp-closing-body">
          Grab the Parabo Press app, pick your photos and start tapping colors.
        </p>
        <AppCta location="closing" />
      </div>
      <PrintStage
        prints={CLOSING_PRINTS}
        stage={CLOSING_STAGE}
        sizes="(max-width: 991px) 40vw, 200px"
        className="csp-closing-stage"
      />
    </Container>
  </section>
);

const Faq = () => (
  <section className="csp-section csp-faq">
    <Container>
      <ProductFaq
        eyebrow="Good to know"
        title="Questions"
        intro={false}
        items={FAQ.map(({ question, answer }) => ({ caption: question, description: answer }))}
      />
    </Container>
  </section>
);

const ColorfulSquarePrintsPage = () => (
  <>
    <Head
      title="Colorful Square Prints with Colored Borders"
      description="Colorful Square Prints: add a colored border to your prints in the Parabo Press iOS app. Pick a shade for each print or let the app suggest colors. Same price."
      image={`${SETTINGS.HOST}/seo/products/square-prints.jpg`}
    />

    <ScriptMarkupFaq items={FAQ} />
    <ScriptMarkupVideo
      name="Colorful Square Prints: Suggest Colors in the Parabo Press app"
      description={DEMO_VIDEO_ALT}
      thumbnailUrl={getCloudinaryUrl({
        imageKey: DEMO_VIDEO_KEY,
        prefix: 'products/',
        type: 'big',
        format: 'jpg',
      })}
      contentUrl={getCloudinaryUrl({
        imageKey: DEMO_VIDEO_KEY,
        prefix: 'products/',
        format: 'mp4',
      })}
      uploadDate={DEMO_VIDEO_UPLOAD_DATE}
      duration={DEMO_VIDEO_DURATION}
    />

    <Layout>
      <div className="lnd csp">
        <Hero />
        <Facts />
        <HowItWorks />
        <Palette />
        <Closing />
        <Faq />
        <NewsletterTile />
      </div>
    </Layout>
  </>
);

export default ColorfulSquarePrintsPage;
